import React, { useEffect, useMemo, useState } from 'react';
import { Box, Chip, MenuItem, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import InventoryMovementServ, {
    INVENTORY_MOVEMENT_LABELS,
    InventoryMovement,
    InventoryMovementItem,
    InventoryMovementType,
    getMovementDate,
    monthOf
} from '@api/Inventory/InventoryMovement';
import { formatPeriod } from '@utils/Expenses';
import InventoryServ from '@api/Inventory/InventoryServ';
import InputsService from '@api/Inputs/InputCtrl';
import { Input } from '@api/Inputs/Input';
import { ResponsiveDataGrid, ResponsiveGridColDef } from '@components/Utilities/Tables/ResponsiveDataGrid';
import { hideLoader, showLoader } from '@utils/Loader';
import { showAlertError } from '@utils/Alerts/Error';
import { showAlertMessage } from '@utils/Alerts/Message';
import { showIOSAlert } from '@utils/Alerts/Confirm';
import { usePermissionSubject } from '@hooks/usePermissionSubject';
import { can, Capability } from '@utils/Permissions/Capabilities';
import { currencyFormatter } from '@utils/Numbers';
import { Button } from '@mui/material';

// ============================================================================
// HISTORIAL DE MOVIMIENTOS DE LA SUCURSAL (F5 bloque ②).
//
// Por qué existe: hasta ahora la sucursal solo mostraba un SALDO, sin manera de
// saber cómo llegó ahí. El CEDIS sí tenía su historial
// (`distribution-center-inventory-history`); la sucursal, nada. Y la lista de
// órdenes del Dashboard no lo resuelve: responde "qué vendimos", no "por qué
// tengo 3 kg de pollo si ayer tenía 8" — las recepciones del CEDIS, la merma y
// los conteos NO son ventas.
//
// Lee el ledger `inventory-movements`, que es la fuente de verdad del saldo.
// ============================================================================

const typeColor: Record<InventoryMovementType, 'default' | 'success' | 'warning' | 'info' | 'error'> = {
    [InventoryMovementType.Sale]: 'default',
    [InventoryMovementType.SaleRevert]: 'info',
    [InventoryMovementType.Reception]: 'success',
    [InventoryMovementType.Adjustment]: 'warning',
    [InventoryMovementType.Count]: 'info',
    [InventoryMovementType.Purchase]: 'success',
    [InventoryMovementType.Shrinkage]: 'error'
};

interface ListInventoryMovementsProps {
    branchOfficeId: string;
}

const ListInventoryMovements: React.FunctionComponent<ListInventoryMovementsProps> = ({ branchOfficeId }) => {
    const inputsService = useMemo(() => new InputsService(), []);
    // Reconstruir: los dos admins, cada uno en su alcance (plan de inventario F2)
    const canRebuild = can(usePermissionSubject(), Capability.InventoryRebuild, { branchOfficeId });
    const [movements, setMovements] = useState<InventoryMovement[]>([]);
    const [inputs, setInputs] = useState<Input[]>([]);
    const [inputFilter, setInputFilter] = useState<string>('all');
    const [typeFilter, setTypeFilter] = useState<string>('all');
    // Un mes a la vez (plan de inventario F4): por `monthKey`, ~750 docs por mes con todo
    // encendido, en vez del ledger entero de la sucursal (~9 k al año)
    const months = useMemo(() => [monthOf(new Date()), monthOf(new Date(), -1), monthOf(new Date(), -2)], []);
    const [month, setMonth] = useState<string>(months[0]);

    const getData = async () => {
        showLoader();
        try {
            const [movementsData, inputsData] = await Promise.all([
                InventoryMovementServ.getMovements(branchOfficeId, {
                    month,
                    inputId: inputFilter === 'all' ? undefined : inputFilter,
                    type: typeFilter === 'all' ? undefined : (typeFilter as InventoryMovementType)
                }),
                inputs.length ? Promise.resolve(inputs) : inputsService.getInputs()
            ]);
            setMovements(movementsData);
            setInputs(inputsData);
        } catch (error: any) {
            showAlertError(error);
        }
        hideLoader();
    };

    useEffect(() => {
        void getData();
        // eslint-disable-next-line
    }, [branchOfficeId, inputFilter, typeFilter, month]);

    /**
     * Rebuild (F5 ③): replica el ledger desde el último conteo físico. Es la red de
     * seguridad cuando un contador quedó torcido (una recepción doble del código
     * viejo, un descuento a medias). Se muestra PRIMERO lo que cambiaría (dryRun) y
     * se pide confirmación: reescribe saldos, no es un refresh.
     */
    const onRebuild = async () => {
        showLoader();
        try {
            const preview = await InventoryServ.rebuildBranchInventory(branchOfficeId, true);
            hideLoader();
            const cambios = preview.balances.filter((balance) => balance.difference !== 0);
            if (cambios.length === 0) {
                showAlertMessage('El inventario ya coincide con el historial: no hay nada que corregir');
                return;
            }
            const detalle = cambios
                .slice(0, 12)
                .map(
                    (balance) =>
                        `• ${balance.inputName}: ${balance.previousTotalUnits} → ${balance.totalUnits} ${balance.unit}`
                )
                .join('\n');
            showIOSAlert({
                title: 'Reconstruir inventario',
                message: `Se corregirán ${cambios.length} insumo(s) según el historial:\n\n${detalle}${
                    cambios.length > 12 ? `\n… y ${cambios.length - 12} más` : ''
                }`,
                buttons: [
                    {
                        text: 'Reconstruir',
                        onPress: async () => {
                            showLoader();
                            try {
                                const result = await InventoryServ.rebuildBranchInventory(branchOfficeId);
                                const corregidos = result.balances.filter(
                                    (balance) => balance.difference !== 0
                                ).length;
                                showAlertMessage(`Inventario reconstruido: ${corregidos} insumo(s) corregidos`);
                                await getData();
                            } catch (error: any) {
                                showAlertError(error?.response?.data?.message || error);
                            }
                            hideLoader();
                        }
                    }
                ]
            });
        } catch (error: any) {
            hideLoader();
            showAlertError(error?.response?.data?.message || error);
        }
    };

    /** Resumen legible de los renglones: "Alitas −7 pza · Papas −200 gr". */
    const renderItems = (movement: InventoryMovement) => {
        const items = movement.items || [];
        // Un conteo puede traer TODOS los insumos: mostrar solo los descuadrados,
        // que es lo único accionable (los que cuadran salen en el tooltip)
        const relevant =
            movement.type === InventoryMovementType.Count ? items.filter((item) => item.quantity !== 0) : items;
        const shown = relevant.slice(0, 4);
        const rest = relevant.length - shown.length;

        if (relevant.length === 0) {
            return (
                <Typography variant="body2" color="text.secondary">
                    {items.length} insumo(s) contados, todo cuadró
                </Typography>
            );
        }

        // Una merma trae su importe (F2); un primer conteo es punto de partida, no descuadre
        const detail = (item: InventoryMovementItem) =>
            item.amount ? ` (${currencyFormatter(item.amount)})` : item.baseline ? ' · primer conteo' : '';

        return (
            <Tooltip
                title={relevant
                    .map((item) => `${item.inputName}: ${item.quantity > 0 ? '+' : ''}${item.quantity} ${item.unit}${detail(item)}`)
                    .join(' · ')}
            >
                <Stack direction="row" flexWrap="wrap" columnGap={1}>
                    {shown.map((item, index) => (
                        <Typography key={index} variant="body2" component="span">
                            {item.inputName}{' '}
                            <Typography
                                component="span"
                                variant="body2"
                                fontWeight={700}
                                color={item.quantity < 0 ? 'error.main' : 'success.main'}
                            >
                                {item.quantity > 0 ? '+' : ''}
                                {item.quantity} {item.unit}
                            </Typography>
                            <Typography component="span" variant="caption" color="text.secondary">
                                {detail(item)}
                            </Typography>
                        </Typography>
                    ))}
                    {rest > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            +{rest} más
                        </Typography>
                    )}
                </Stack>
            </Tooltip>
        );
    };

    const columns: ResponsiveGridColDef[] = useMemo(
        () => [
            {
                field: 'date',
                flex: 1,
                showInMobile: true,
                sortable: true,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Fecha
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Typography variant="body2">
                        {getMovementDate(row)
                            ? format(getMovementDate(row) as Date, "d MMM yyyy HH:mm", { locale: es })
                            : '—'}
                    </Typography>
                ),
                valueGetter: ({ row }) => getMovementDate(row)?.getTime() ?? 0
            },
            {
                field: 'type',
                flex: 1,
                showInMobile: true,
                sortable: true,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Movimiento
                    </Typography>
                ),
                renderCell: ({ row }) => (
                    <Chip
                        size="small"
                        label={INVENTORY_MOVEMENT_LABELS[row.type as InventoryMovementType] || row.type}
                        color={typeColor[row.type as InventoryMovementType] || 'default'}
                        variant={row.type === InventoryMovementType.Sale ? 'outlined' : 'filled'}
                    />
                ),
                valueGetter: ({ row }) => row.type
            },
            {
                field: 'items',
                flex: 3,
                showInMobile: true,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Insumos
                    </Typography>
                ),
                renderCell: ({ row }) => renderItems(row)
            },
            {
                field: 'note',
                flex: 1,
                showInMobile: false,
                sortable: false,
                disableColumnMenu: true,
                renderHeader: () => (
                    <Typography color="#809FB8" fontWeight={600}>
                        Motivo / Origen
                    </Typography>
                ),
                // El movimiento de venta trae el FOLIO en `note` (lo pone el
                // server): así la línea se entiende sin ir a buscar el pedido
                renderCell: ({ row }) => (
                    <Typography variant="body2" color={row.orderId ? 'text.primary' : 'text.secondary'}>
                        {row.note || '—'}
                    </Typography>
                )
            }
        ],
        []
    );

    return (
        <Stack height="100%" spacing={2} p={2}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                <TextField select size="small" label="Mes" value={month} onChange={(e) => setMonth(e.target.value)} sx={{ minWidth: 160 }}>
                    {months.map((value) => (
                        <MenuItem key={value} value={value}>
                            {formatPeriod(value)}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Insumo"
                    value={inputFilter}
                    onChange={(e) => setInputFilter(e.target.value)}
                    sx={{ minWidth: 220 }}
                >
                    <MenuItem value="all">Todos los insumos</MenuItem>
                    {inputs.map((input) => (
                        <MenuItem key={input.id} value={input.id}>
                            {input.name}
                        </MenuItem>
                    ))}
                </TextField>
                <TextField
                    select
                    size="small"
                    label="Tipo"
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    sx={{ minWidth: 200 }}
                >
                    <MenuItem value="all">Todos los movimientos</MenuItem>
                    {Object.values(InventoryMovementType).map((type) => (
                        <MenuItem key={type} value={type}>
                            {INVENTORY_MOVEMENT_LABELS[type]}
                        </MenuItem>
                    ))}
                </TextField>
                <Box flex={1} />
                <Typography variant="caption" color="text.secondary">
                    {movements.length} movimiento(s) en {formatPeriod(month)}
                </Typography>
                {canRebuild && (
                    <Button size="small" variant="outlined" onClick={onRebuild}>
                        Reconstruir inventario
                    </Button>
                )}
            </Stack>

            <Box flex={1} minHeight={0}>
                <ResponsiveDataGrid rows={movements} columns={columns} getRowId={(row: any) => row.id} />
            </Box>
        </Stack>
    );
};

export default ListInventoryMovements;
